// COMISIÓN POR MIEMBRO DE LA PÓLIZA.
//
// Lissette, 2026-09-11: *"en el tipo de póliza de salud nos pagan por miembros
// en la póliza: agrega una columna con el número de personas aseguradas —y que
// lo traiga automático de la ficha de la póliza—, al lado cuánto es por persona
// la comisión, al lado el porcentaje que recibimos, al lado si es split o
// agencia, y el total va a ser el porcentaje que nos toca multiplicado por la
// cantidad de personas"*.
//
// LA CUENTA, confirmada con ella con un ejemplo antes de escribir nada:
//
//     4 asegurados × $20 por persona = $80 que paga la aseguradora
//     $80 × 50% (split)              = $40 que nos quedan   <- el total
//
// Es DISTINTA de la que ya tenía esta tabla (mensual × meses de vigencia), y por
// eso convive con ella en vez de sustituirla: la de por miembro manda solo
// cuando la póliza tiene personas Y un importe por persona. Sin esos dos datos
// no hay nada que calcular y se sigue usando la de siempre, así que ninguna
// póliza de las que ya están cambia de total sola.
//
// Vive suelto, sin React, para poder probar la aritmética del dinero por lo que
// DEVUELVE. Una cuenta de dinero metida dentro de un componente solo se puede
// comprobar mirándola.

import { distinctLabels } from './utils.js';

/** Lo que se ofrece de entrada en el desplegable de la razón. No es una lista
 *  cerrada: lo que se escriba se queda (ver `razonesDisponibles`). */
export const RAZONES_POR_DEFECTO = ['Split', 'Agencia'];

/**
 * Cuántas personas están aseguradas en esta póliza.
 *
 * Es el MISMO número que enseña el formulario de la póliza en "Miembros
 * activos": las personas marcadas del hogar (`policyInsured`) más las escritas
 * a mano (`policyInsuredExtra`). Se cuenta de ahí y no de `membersActive` —que
 * existe en las fichas viejas— porque ese campo se quedó congelado cuando el
 * formulario pasó a calcularlo, y cobrar por un número congelado es cobrar mal.
 */
export function personasAseguradas(policy) {
  const marcadas = Array.isArray(policy?.policyInsured) ? policy.policyInsured.filter(Boolean).length : 0;
  const extra = Array.isArray(policy?.policyInsuredExtra)
    ? policy.policyInsuredExtra.filter((n) => String(n ?? '').trim()).length
    : 0;
  return marcadas + extra;
}

/** Un número de una casilla, tolerando "$", comas y espacios. */
export const numero = (v) => {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.\-]/g, ''));
  return isNaN(n) ? 0 : n;
};

/**
 * El total de una póliza que se cobra por miembro.
 *
 * SIN PORCENTAJE SE ENTIENDE 100%, y no 0: una casilla vacía significa "no hay
 * split, nos queda todo", que es el caso normal. Multiplicar por cero lo que
 * está en blanco pondría $0 en una fila que sí cobra, y un cero calculado no se
 * distingue de un cero de verdad.
 */
export function comisionPorMiembro({ personas, porPersona, porcentaje }) {
  const n = Math.max(0, Math.trunc(numero(personas)));
  const unitario = numero(porPersona);
  const pct = numero(porcentaje);
  const parte = pct > 0 ? pct / 100 : 1;
  return Math.round(n * unitario * parte * 100) / 100;
}

/**
 * ¿Manda la cuenta por miembro en esta fila? Hacen falta LAS DOS cosas: a quién
 * se asegura y cuánto se cobra por cada uno. Con una sola, el total sería $0 y
 * la tabla estaría diciendo que esa póliza no deja nada.
 */
export function cobraPorMiembro({ personas, porPersona }) {
  return Math.trunc(numero(personas)) > 0 && numero(porPersona) > 0;
}

/**
 * Lo que ofrece el desplegable de la razón: las de siempre más TODO lo que ya
 * se haya escrito en esa columna. Deducirlas de lo escrito es la única copia
 * que no se puede desincronizar — la misma decisión que se tomó con las
 * columnas propias y con el tipo de suplemento.
 */
export function razonesDisponibles(valoresEscritos) {
  return distinctLabels([...RAZONES_POR_DEFECTO, ...(valoresEscritos || [])]);
}
